import type { CategoryKgCo2ePointDto } from '@siliconx/shared';

/**
 * SIL-440 — the five GHG-category buckets the SIL-435 kernel emits, with
 * their SIL-426 §4 labels (VERBATIM — see `carbonCopy.ts`'s rules; these live
 * here rather than there only because label + helper + series colour are one
 * indivisible per-category record) and the chart's colour assignment.
 *
 * Category ORDER is load-bearing: it is the stack order of the area chart
 * (bottom -> top) and the chip order beneath it, and it is deliberately
 * operational-first (scope2, cat8) then one-time lumps (cat1_or_2, cat5,
 * cat4) so a reader's eye reads the recurring rate as the base of the stack
 * and each lump as a spike above it.
 */
export type CarbonCategoryKey = keyof CategoryKgCo2ePointDto;

export const CARBON_CATEGORY_ORDER = [
  'scope2',
  'cat8',
  'cat1_or_2',
  'cat5',
  'cat4',
] as const satisfies readonly CarbonCategoryKey[];

export interface CarbonCategoryMeta {
  /** SIL-426 §4 label — verbatim. */
  label: string;
  /**
   * SIL-426 §4 helper. Present only for the three categories the inventory
   * gives one; `undefined` means the inventory deliberately ships the label
   * alone (never invent a helper to fill the gap).
   */
  helper?: string;
  /** Series/chip colour. Violet-indigo-fuchsia family = the FORECAST card's
   * own palette (`ScenarioCarbonForecast`'s violet treatment); NEVER the
   * sky/cyan family, which is reserved for the quarantined advisory module. */
  color: string;
}

export const CARBON_CATEGORY_META: Record<CarbonCategoryKey, CarbonCategoryMeta> = {
  scope2: {
    label: 'Scope 2 · electricity (operated facilities)',
    color: '#7c3aed', // violet-600
  },
  cat8: {
    label: 'Category 8 · electricity (colocation)',
    helper:
      'Colocation electricity is assigned to Category 8 (upstream leased assets) by platform convention; depending on your lease terms and consolidation approach it may belong in Scope 2. Disclosed in the methodology sheet.',
    color: '#818cf8', // indigo-400
  },
  cat1_or_2: {
    label: 'Category 1/2 · purchased hardware (embodied)',
    helper:
      "Categorized per your organization's fixed-asset treatment (set in Financials setup).",
    color: '#d946ef', // fuchsia-500
  },
  cat5: {
    label: 'Category 5 · end-of-life disposal',
    helper: 'Expected future emissions from disposal actions taken in the month shown.',
    color: '#c4b5fd', // violet-300
  },
  cat4: {
    label: 'Category 4 · transport (moves and used purchases)',
    color: '#a5b4fc', // indigo-300
  },
};

/** Sum of every category in one point (kgCO2e). */
export function categoryPointTotal(p: CategoryKgCo2ePointDto): number {
  return p.scope2 + p.cat8 + p.cat1_or_2 + p.cat5 + p.cat4;
}

/** kgCO2e -> tCO2e. The whole carbon card renders tonnes because the target
 * (`tco2ePerYr`) and the abatement ranking ($/tCO2e) are tonne-denominated;
 * mixing units against those two would invite a 1000x misread. */
export function kgToTonnes(kg: number): number {
  return kg / 1000;
}

/** Tonnes, formatted for display. Three significant-ish decimals at small
 * magnitudes so a sub-tonne lump (a disposal adder is often ~0.2 t) never
 * renders as a bare "0 t" — an honest small number, never a rounded-away one. */
export function formatTonnes(t: number): string {
  const abs = Math.abs(t);
  const maximumFractionDigits = abs === 0 ? 0 : abs < 1 ? 3 : abs < 100 ? 1 : 0;
  return `${t.toLocaleString('en-US', { maximumFractionDigits })} tCO2e`;
}
